import { RemovableCard, Text } from '@dixa/lib-shared-habla';
import type { CreateConversationMessageBulkApiArg } from 'lib-shared-types-api';
import { useCallback, useEffect, useState } from 'react';

import { ConversationsBulkTextEditor } from 'components/conversation';

import {
  BulkModalActionBaseProps,
  BulkModalSendReplyAction,
} from '../../../types/conversationsBulkModal.types';
import { conversationsBulkModalActionLabel } from '../../../utils/conversationsBulkModalActionLabel';

type ConversationsBulkModalSendReplyActionProps =
  BulkModalActionBaseProps<BulkModalSendReplyAction>;

/** Represents the component for the conversations bulk modal send reply action. */
export const ConversationsBulkModalSendReplyAction = ({
  conversationIds,
  onChange,
  onRemove,
}: ConversationsBulkModalSendReplyActionProps): JSX.Element => {
  const [{ htmlText, plainText }, setText] = useState<
    Omit<CreateConversationMessageBulkApiArg, 'csids'>
  >({
    htmlText: '',
    plainText: '',
  });

  useEffect(
    () =>
      onChange({
        type: 'send_reply',
        isValid: htmlText.trim().length > 0 && plainText.trim().length > 0,
        arg: {
          csids: conversationIds,
          htmlText,
          plainText,
        },
      }),
    [conversationIds, htmlText, onChange, plainText],
  );

  const handleChange = useCallback((htmlText: string, plainText: string) => {
    setText({
      htmlText,
      plainText,
    });
  }, []);

  return (
    <RemovableCard
      icon="paper-plane"
      title={conversationsBulkModalActionLabel['send_reply']}
      onRemove={() => onRemove('send_reply')}
    >
      <Text.Paragraph subdued withMarginBottom>
        A reply will be addressed to the same receivers as in the last message
        and be sent from the latest selected sender in the conversation. Sending
        a reply in bulk will not change the assignee or close the conversation
        after the message is sent. If you would like to close the selected
        conversations as well, then please add a “Close” action here.
      </Text.Paragraph>

      <ConversationsBulkTextEditor type="send_reply" onChange={handleChange} />
    </RemovableCard>
  );
};
